import { Component, OnInit, Output, EventEmitter, inject, Renderer2, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { SiteStateService } from '../../../core/services/site-state.service';
import { ClientAccessScopeService } from '../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../core/services/menu-permission.service';
import { SidebarStateService } from '../../../core/services/sidebar-state.service';
import { MenuNavigatorService } from '../../../core/services/menu-navigator.service';
import { PssConfirmModalComponent } from '../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalMyProfileComponent } from '../../../pages/private/modals/auth/modal-my-profile/modal-my-profile.component';
import { ApiAuthService } from '../../../core/services/api/auth/api-auth.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, PssConfirmModalComponent, ModalMyProfileComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit {
  @Output() toggleSidebar = new EventEmitter<void>();

  showDropdown = false;
  disableHeader = false;
  isLogoutModalOpen = false;
  isProfileModalOpen = false;
  isDarkMode = false;
  name: string = '';
  role: string = '';
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private siteState = inject(SiteStateService);
  private clientScope = inject(ClientAccessScopeService);
  private permissionService = inject(MenuPermissionService);
  readonly sidebarState = inject(SidebarStateService);
  private apiAuth = inject(ApiAuthService);
  private menuNavigator = inject(MenuNavigatorService);

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.name = this.cookieService.get('name');
  }

  // ================= Normal Functions =================

  ngOnInit(): void {
    const splitPath = this.router.url.split('/');
    this.disableHeader = splitPath[splitPath.length - 2] === 'invoice-fulltax';

    // Check Dark Mode
    if (isPlatformBrowser(this.platformId)) {
      const storedTheme = localStorage.getItem('theme');
      if (
        storedTheme === 'dark' ||
        (!storedTheme &&
          window.matchMedia('(prefers-color-scheme: dark)').matches)
      ) {
        this.setDarkTheme(true);
      }
    }
  }

  toggleTheme(): void {
    this.setDarkTheme(!this.isDarkMode);
  }

  setDarkTheme(isDark: boolean): void {
    this.isDarkMode = isDark;
    if (isDark) {
      this.renderer.addClass(this.document.documentElement, 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      this.renderer.removeClass(this.document.documentElement, 'dark');
      localStorage.setItem('theme', 'light');
    }
  }

  /** เปิดหน้าต่างค้นหาเมนูทั้งระบบ (เหมือนกด Ctrl + K) */
  openMenuPalette(): void {
    this.menuNavigator.openPalette();
  }

  /**
   * Toggles the lock/pinned state of the sidebar.
   */
  toggleSidebarLock(): void {
    this.sidebarState.toggleSidebarLock();
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  openProfileModal(): void {
    this.showDropdown = false;
    this.isProfileModalOpen = true;
  }

  closeProfileModal(): void {
    this.isProfileModalOpen = false;
  }

  onProfileSaved(displayName: string): void {
    this.name = displayName;
    this.closeProfileModal();
  }

  openLogoutModal(): void {
    this.showDropdown = false;
    this.isLogoutModalOpen = true;
  }

  closeLogoutModal(): void {
    this.isLogoutModalOpen = false;
  }

  confirmLogout(): void {
    this.closeLogoutModal();
    this.apiAuth.logout().pipe(finalize(() => this.finishLogout())).subscribe({
      next: () => undefined,
      error: () => undefined,
    });
  }

  private finishLogout(): void {
    this.siteState.clear();
    this.clientScope.clear();
    this.permissionService.resetSession();
    this.clearAuthCookies();
    this.clearAuthLocalStorage();
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  /** Cookies must be deleted with the same path used when set (default `/`). */
  private clearAuthCookies(): void {
    const path = '/';
    const keys = ['token', 'user_id', 'username', 'name', 'company_id', 'must_change_password'];
    for (const key of keys) {
      this.cookieService.delete(key, path);
    }
    // Fallback for any other app cookies on `/`
    this.cookieService.deleteAll(path);
  }

  private clearAuthLocalStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      localStorage.removeItem('userId');
      localStorage.removeItem('user_id');
    } catch {
      // ignore quota / private-mode errors
    }
  }

  // ================= API Functions =================
}
